import { fieldLimits } from "config";
import type { TFunction } from "i18next";
import { maxNameParts, splitNameParts } from "utilities";
import { AnyObjectSchema, bool, object, ref, string } from "yup";

import type { ForgotPasswordModel, LoginFormModel, RegisterFormModel, ResetPasswordModel } from "./models";

const MIN_PASSWORD_LENGTH = 8;

/**
 * Validation messages are user-facing copy, so the schemas are built per render from the
 * `accountsPage` namespace rather than held as module constants. Each page memoises the result on
 * `t` so Formik is not handed a new schema on every keystroke.
 */
export const createLoginFormSchema = (t: TFunction): AnyObjectSchema =>
	object<LoginFormModel>().shape({
		email: string()
			.required(t("validation.emailRequired"))
			.email(t("validation.emailInvalid"))
			.max(fieldLimits.email, t("validation.emailTooLong", { max: fieldLimits.email })),
		password: string().required(t("validation.passwordRequired"))
	});

export const createForgotPasswordSchema = (t: TFunction): AnyObjectSchema =>
	object<ForgotPasswordModel>().shape({
		email: string()
			.required(t("validation.emailRequired"))
			.email(t("validation.emailInvalid"))
			.max(fieldLimits.email, t("validation.emailTooLong", { max: fieldLimits.email }))
	});

export const createResetPasswordSchema = (t: TFunction): AnyObjectSchema =>
	object<ResetPasswordModel>().shape({
		confirmPassword: string()
			.required(t("validation.confirmPasswordRequired"))
			.oneOf([ref("newPassword")], t("validation.passwordsMustMatch")),
		newPassword: string()
			.required(t("validation.passwordRequired"))
			.min(MIN_PASSWORD_LENGTH, t("validation.passwordTooShort", { min: MIN_PASSWORD_LENGTH }))
			.max(fieldLimits.password, t("validation.passwordTooLong", { max: fieldLimits.password }))
	});

export const createRegisterFormSchema = (t: TFunction): AnyObjectSchema =>
	object<RegisterFormModel>().shape({
		// `oneOf([true])` rather than `required()`: an unticked box is a present value, just the
		// wrong one, so required() would let it through.
		acceptedTerms: bool().oneOf([true], t("validation.termsRequired")),
		email: string()
			.required(t("validation.emailRequired"))
			.email(t("validation.emailInvalid"))
			.max(fieldLimits.email, t("validation.emailTooLong", { max: fieldLimits.email })),
		fullName: string()
			.required(t("validation.fullNameRequired"))
			.max(fieldLimits.fullName, t("validation.fullNameTooLong", { max: fieldLimits.fullName }))
			.test(
				"name-parts",
				t("validation.fullNameTooManyParts", { max: maxNameParts }),
				(value?: string) => splitNameParts(value ?? "").length <= maxNameParts
			),
		password: string()
			.required(t("validation.passwordRequired"))
			.min(MIN_PASSWORD_LENGTH, t("validation.passwordTooShort", { min: MIN_PASSWORD_LENGTH }))
			.max(fieldLimits.password, t("validation.passwordTooLong", { max: fieldLimits.password }))
	});
